/* ============================================================================
 * Raisy /pitchdeck — V2 (the proposed build).
 *
 * V1  = `slides.tsx` — the co-founder's PDF ported page-for-page (what is live
 *       at raisy.team/pitchdeck today, incl. D-055 USD + D-056 revenue ladder).
 * V2  = V1 + the improvements from the 2026-09-13 per-slide challenge:
 *       · hook split back into two beats, with the group photo (p2 / p3)
 *       · the p4 stat card whose attribution could not be verified is cut
 *       · the real product screenshot ported onto "This is what we ship" (p6)
 *       · the expansion-path graphic on the Company-OS wedge slide (p12)
 *       · a 2×2 above the Metal/Platvix table (rule 28), table kept below
 *       · pricing: ★ on Growth, "reachouts" (not "emails"), m12/m24 ladder labels
 *       · the ADVISORS panel restored — its own slide (p15, exists only as a raster)
 *       · the use-of-proceeds donut restored on the ask (p16)
 *
 * Unchanged slides are IMPORTED from V1 so the two versions cannot drift.
 * Audit: raise/supercharger/02_20260817_supercharger_pitch/RAISY_DECK_AUDIT_20260913.md
 * Decisions: project109 D-054 (port) · D-055 (USD) · D-056 (ladder) · D-057 (V2 route)
 * ========================================================================== */
import { motion } from 'framer-motion';
import { ReactElement, RefObject, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  CountUp,
  Kicker,
  S1Hook,
  S1HookReveal,
  S6TrustLayer,
  S7Standards,
  S17Impressum,
  S18CompanyOS,
  Shell,
  Stat,
  fadeUp,
  pop,
  stagger,
} from './slides';
import {
  MARKET_CAGR,
  MARKET_CUSTOMERS,
  MARKET_SAM,
  MARKET_SAM_BASIS,
  MARKET_SOM,
  MARKET_SOM_GROWTH,
  MARKET_TAM,
  MARKET_TAM_GROWTH,
  MARKET_YEARS,
} from './marketByYear';
import {
  EXIT_EV_MULTIPLE,
  EXIT_EV_MULTIPLE_BAND,
  EXIT_HORIZON_YEARS,
  EXIT_MULTIPLE_REQUIRED,
  GTM_BUDGET,
  GTM_CAC_CEILING,
  GTM_CAC_HEADROOM_PCT,
  GTM_CAC_TODAY,
  GTM_CUSTOMERS_THE_ROUND_BUYS,
  M12_ARR,
  M12_CUSTOMERS,
  MIN_TICKET_SIZE,
  POST_MONEY_VALUATION,
  PRE_MONEY_VALUATION,
  PROCEEDS,
  PROCEEDS_ROUND,
  ROUND_EQUITY_PCT,
  ROUND_MOIC,
  ROUND_RUNWAY_MONTHS,
  TICKET_EQUITY_PCT,
  TICKET_SIZE,
} from './deckFinancials';
import { MarketByYearTable } from './marketTable';

/* ========================================================================
 * PDF p4 · the era — the uncitable "70–80% odds" card is cut (audit A10);
 * the two sourced stats stay.
 * ====================================================================== */
export function S2EraV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_network.jpg', alt: 'The network of one-person companies', cap: 'the network' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Analyzing the market shift…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The era of the one-person company is here.
          <small>The founders who adopt AI properly will out-compete everyone else.</small>
        </motion.h1>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginTop: 26 }} variants={stagger}>
          <Stat
            n={<CountUp to={36} suffix="%" duration={3.2} />}
            l="Solo-founded of new startups"
            s="Carta, Founder Ownership Report 2026"
          />
          <Stat
            n={<CountUp to={89} suffix="%" duration={3.2} />}
            l="say AI is critical to survival"
            s="Alibaba.com CoCreate Pitch 2026"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p5 + p12 · the wedge, with the expansion-path graphic.
 * ====================================================================== */
const EASEX = [0.22, 1, 0.36, 1] as const;

/** PDF p12's "EXPANSION PATH" (Fundraising wedge → Sales → Recruiting → Support → IR).
 *  Its raster is 1376×449, so in a side column it cropped to "→ Recruiting" — the
 *  deck's own rail renders it whole. */
const EXPANSION = [
  { num: '01', title: 'Fundraising', desc: 'the wedge — every founder raises', hero: true },
  { num: '02', title: 'Sales', desc: 'the same memory, a new motion' },
  { num: '03', title: 'Recruiting', desc: 'sourcing, screening, scheduling' },
  { num: '04', title: 'Support', desc: 'triage, answers, case notes' },
  { num: '05', title: 'IR', desc: 'updates, asks, reporting' },
];

export function S3CompanyOSV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Declaring the category…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Raisy is the Company OS for founders.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          <b>Fundraising is the wedge.</b> Every founder needs to raise. Once we're in,
          Raisy becomes the operating system for the entire company.
        </motion.p>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.2, ease: EASEX, delay: 0.8 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {EXPANSION.map((m, i) => (
              <motion.div
                key={m.title}
                className={cn('pd-jnode', m.hero && 'hero')}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASEX, delay: 0.9 + i * 0.3 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
          <div className="pd-jpath">
            <b>The wedge gets us in</b> — the same memory then runs every function.
          </div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p6 · what we ship — the shipped loop, as the deck's own animated rail
 * (was: four small boxes nobody looked at), over X's pipeline artwork.
 * ====================================================================== */
const PIPE_V2 = [
  { st: '01', b: 'Investor research', i: 'scored from the web' },
  { st: '02', b: 'Personalized outreach', i: 'written from your kernel' },
  { st: '03', b: 'Human approval', i: 'nothing leaves without you' },
  { st: '04', b: 'Send + track', i: 'opens, replies, follow-ups' },
];

export function S4ShipV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_pipeline.jpg', alt: 'The shipped Raisy pipeline', cap: 'the pipeline' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Running the pipeline…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          This is what we ship.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp} style={{ fontSize: 'clamp(12px,1.3vw,15px)' }}>
          One loop, already running: research → outreach → your approval → send, tracked.
        </motion.p>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2, ease: EASEX, delay: 0.7 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {PIPE_V2.map((p, i) => (
              <motion.div
                key={p.st}
                className="pd-jnode"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.85, ease: EASEX, delay: 0.8 + i * 0.34 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{p.st}</div>
                <div className="pd-jtitle">{p.b}</div>
                <div className="pd-jdesc">{p.i}</div>
              </motion.div>
            ))}
          </div>
          <div className="pd-jpath">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              style={{ marginRight: 12 }}
            >
              <b>Audit trail</b> — every step logged, every decision receipted.
            </motion.span>
          </div>
        </motion.div>
        <motion.div className="pd-chips" variants={stagger} style={{ marginTop: 20 }}>
          <motion.span className="pd-chip" variants={pop}>live today · v1.0</motion.span>
          <motion.span className="pd-chip muted" variants={pop}>3,552 investors in range</motion.span>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p10–11 · they sell a tool — 2×2 (two differentiators, rule 28) ABOVE
 * the co-founder's table, which is kept for the specifics.
 * ====================================================================== */
const MAP2 = [
  { t: 'OpenVC', x: 16, y: 14 },
  { t: 'Metal', x: 40, y: 22 },
  { t: 'Platvix', x: 50, y: 36 },
  { t: 'Generic AI tools', x: 64, y: 76 },
  { t: 'Raisy', x: 86, y: 14, hero: true },
];

const COMPARE_V2: Array<[string, string, string, string]> = [
  ['What it is', 'Fundraising CRM', 'Fundraising engine', 'Company OS'],
  ['Continues after the round', 'No', 'No', 'Yes — memory persists'],
  ['Expands to sales/recruiting', 'No', 'No', 'Yes'],
  ['Pricing (annual)', '$2,400+', 'Free (paid tier not public)', '~$1,000 (Growth tier)'],
  ['Governance', 'None', 'Basic', 'Full (ISO, GDPR, audit trail)'],
];

export function S8ToolVsOSV2({ dir }: { dir: number }) {
  const hero = MAP2[MAP2.length - 1];
  return (
    <Shell custom={dir} className="pd-s8">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Mapping the field…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp} style={{ marginBottom: 8 }}>
          They sell a tool. We build the operating system.
        </motion.h1>
        {/* .pd-s8 .pd-map is fixed at 48vh; X+1 puts the table underneath, so the
            map gets a bounded height here and the slide stays inside the viewport. */}
        <motion.div className="pd-map" variants={fadeUp} style={{ height: 320, marginBottom: 10 }}>
          <div className="cross" />
          <div className="cross v" />
          <span className="axis-y">Built for a raise ↑</span>
          <span className="axis-x">Does the work for you →</span>
          <span className="quad tl">lists, no send</span>
          <span className="quad tr">the gap → Raisy</span>
          <span className="quad bl">generic tools</span>
          <span className="quad br">sends, not for a raise</span>
          {MAP2.map((p) => (
            <div key={p.t} className={cn('dot', p.hero && 'hero')} style={{ left: `${p.x}%`, top: `${p.y}%` }}>
              <div className="pin" />
              <b>{p.t}</b>
            </div>
          ))}
          <span style={{ display: 'none' }}>{hero.t}</span>
        </motion.div>
        <motion.div className="pd-econ" variants={fadeUp}>
          <table className="econ-tbl">
            <thead>
              <tr>
                <th></th>
                <th>Metal</th>
                <th>Platvix</th>
                <th>Raisy</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_V2.map(([label, metal, platvix, raisy]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{metal}</td>
                  <td>{platvix}</td>
                  <td className="ok">{raisy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p13 · pricing — ★ on Growth, "reachouts" (the published unit, D-061),
 * and the D-070 ladder spelled out.
 *
 * CORRECTED 2026-10-11 (@P288 T0300): the numerals and the defining quantities were
 * D-052's, which D-053 superseded and D-070 superseded again. D-070 states the published
 * ladder as Free $0 · Starter $49 · Growth $129 · Scale $279 at 5/250/750/1,700, and says
 * in its own consequences: "Any deck printing $149 or $399 is wrong."
 *
 * ⚠️ AND ONE DIVERGENCE LEFT UNFIXED ON PURPOSE: `slidesMaster.tsx` `M12Model` renders
 * FOUR rows (it carries `Free $0 / 5 reachouts`), while this component carries THREE.
 * Adding the Free row is a design change, not the correction D-070 instructed, so it is
 * reported rather than made.
 * ====================================================================== */
const PLANS_V2 = [
  { n: 'Starter', p: '$49', v: '250 reachouts/mo', t: 'Testing' },
  { n: 'Growth', p: '$129', v: '750 reachouts/mo', t: 'Active raise', star: true },
  { n: 'Scale', p: '$279', v: '1,700 reachouts/mo', t: 'High volume' },
];

export function S10PricingV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Calculating the economics…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Five free emails. Then three simple plans.
          <small>Free 5 → Starter → Growth → Scale. One account, round after round.</small>
        </motion.h1>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginTop: 20, maxWidth: 860 }} variants={stagger}>
          <Stat
            n={<CountUp to={50} prefix="$" suffix="K" duration={3} />}
            l={<>MRR target — 1,000 paying × $50/mo by month 12 (<b>$0.6M ARR</b>)</>}
          />
          <Stat
            n={<CountUp to={1.2} prefix="$" suffix="M" decimals={1} duration={3} />}
            l={<>ARR target — <b>by month 24</b> (1,000 accounts at ~$100/mo)</>}
          />
        </motion.div>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 26 }} variants={stagger}>
          {PLANS_V2.map((pl) => (
            <Stat
              key={pl.n}
              n={
                <span>
                  {pl.star && <span style={{ fontSize: 'clamp(12px,1.2vw,15px)', marginRight: 6 }}>★</span>}
                  {pl.p}
                  <span style={{ fontSize: 'clamp(12px,1.2vw,15px)', opacity: 0.7 }}>/mo</span>
                </span>
              }
              l={<><b>{pl.n}</b> — {pl.v} · {pl.t}</>}
            />
          ))}
        </motion.div>
        <motion.p className="pd-foot" variants={fadeUp} style={{ marginTop: 22 }}>
          First 5 emails free. No card. No trial clock.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p16 · THE ASK — V1's ask plus the use-of-proceeds donut restored.
 * ====================================================================== */
/**
 * Donut slices come from the GENERATED ./deckFinancials.ts (the model → deck mirror), so
 * the pie cannot drift from the financial model. The colours stay here: colour is
 * presentation, not a business constant. The split used to be hardcoded in this file,
 * which is why nothing could tell whether the slide still matched the plan (FM-0013 D-024).
 */
const PIE_COLORS = ['#0AB3BF', '#4DD4E0', '#35D0DB', '#6DE6EC'];
const PIE_V2 = PROCEEDS.map((p, i) => ({
  name: p.label,
  value: Math.round(p.pct * 10000) / 100,
  color: PIE_COLORS[i % PIE_COLORS.length]!,
}));

function DonutV2({ active, onActive, size = 320 }: { active: string | null; onActive: (k: string | null) => void; size?: number }) {
  const R = 72;
  const SW = 27;
  const CIRC = 2 * Math.PI * R;
  const GAP = 3.2;
  let pos = 0;
  return (
    <svg className="pd-donut" viewBox="0 0 200 200" aria-hidden style={{ width: size, height: size }}>
      {PIE_V2.map((d) => {
        const frac = d.value / 100;
        const seg = Math.max(frac * CIRC - GAP, 2);
        const on = active === d.name;
        const dimmed = active !== null && !on;
        const offset = CIRC - pos;
        pos += frac * CIRC;
        return (
          <circle
            key={d.name}
            cx={100}
            cy={100}
            r={R}
            fill="none"
            stroke={d.color}
            strokeWidth={SW}
            strokeDasharray={`${seg} ${CIRC - seg}`}
            strokeDashoffset={offset}
            transform="rotate(-90 100 100)"
            style={{
              opacity: dimmed ? 0.22 : 1,
              strokeWidth: on ? SW + 6 : SW,
              cursor: 'pointer',
              transition: 'opacity 0.25s, stroke-width 0.25s',
            }}
            onMouseEnter={() => onActive(d.name)}
            onMouseLeave={() => onActive(null)}
            onClick={() => onActive(on ? null : d.name)}
          />
        );
      })}
      <text x="100" y="95" textAnchor="middle" fill="#eaf6f7" fontSize="27" fontWeight="800">$${(PROCEEDS_ROUND / 1e6).toFixed(1)}M</text>
      <text x="100" y="114" textAnchor="middle" fill="#9db7bc" fontSize="11">pre-seed</text>
    </svg>
  );
}

const FOUNDER_LINKS_V2 = {
  david: { email: 'david@raisy.team', calendar: 'https://calendly.com/david-playano', dataroom: 'https://drive.google.com/drive/folders/raisy-dataraum' },
  simon: { email: 'simon@raisy.team', calendar: 'https://calendly.com/simon-playano', dataroom: 'https://drive.google.com/drive/folders/raisy-dataraum' },
};
const PRIMARY_CTA_V2 = 'Investing';
const SECONDARY_V2 = ['Try the product', 'Build an AI workforce', 'Partnership', 'Referral', 'Other'];

export function S16AskV2({ dir, nameRef }: { dir: number; nameRef: RefObject<HTMLInputElement | null> }) {
  const [picked, setPicked] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const pick = (opt: string) => {
    setPicked(opt);
    void fetch('https://api.raisy.team/api/v1/waitlist/join', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: nameRef.current?.value.trim() || 'anonymous',
        email: 'anonymous@deck',
        code: `INTERESTED:${opt}`,
        tab: 'PitchDeck',
      }),
    }).catch(() => undefined);
    setMsg(opt === PRIMARY_CTA_V2 ? "Thanks — we'll reach out to schedule a partner call." : `Thanks — logged. We'll follow up on ${opt.toLowerCase()}.`);
  };

  return (
    <Shell custom={dir} className="pd-ask">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The ask…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Join the Raisy round
          <small>$${(PROCEEDS_ROUND / 1e6).toFixed(1)}M pre-seed · SAFE · {ROUND_RUNWAY_MONTHS}-month runway · Min ticket $${(MIN_TICKET_SIZE / 1000).toFixed(0)}K</small>
        </motion.h1>
        <motion.p className="pd-terms" variants={fadeUp}>
          <b>1,000</b> paying customers by month 12 · <b>$1.2M</b> ARR target by month 24
        </motion.p>
        {/* Valuation + stake come from the GENERATED ./deckFinancials.ts (D-029): equity is
            an outcome of the locked valuation, so the deck states the price and the stake it
            buys rather than asserting a percentage of its own. */}
        <motion.p className="pd-terms" variants={fadeUp} style={{ marginTop: 2, fontSize: 'clamp(11px,1.1vw,13px)', color: 'var(--pd-dim)' }}>
          <b>${(PRE_MONEY_VALUATION / 1e6).toFixed(2)}M</b> pre-money · <b>${(POST_MONEY_VALUATION / 1e6).toFixed(2)}M</b> post
          (what the SAFE states) · the round buys <b>{(ROUND_EQUITY_PCT * 100).toFixed(2)}%</b> · a ${(TICKET_SIZE / 1000).toFixed(0)}K ticket{' '}
          <b>{(TICKET_EQUITY_PCT * 100).toFixed(2)}%</b> · <b>{ROUND_MOIC.toFixed(1)}×</b> if the year-{EXIT_HORIZON_YEARS} plan lands
        </motion.p>

        <motion.div className="pd-ask-ctas" variants={stagger}>
          <motion.button
            className={cn('pd-cta-main', picked === PRIMARY_CTA_V2 && 'pick')}
            variants={pop}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => pick(PRIMARY_CTA_V2)}
          >
            {PRIMARY_CTA_V2}
            <span>Lead or join the round</span>
          </motion.button>
          <motion.div className="pd-cta-secondary" variants={stagger}>
            {SECONDARY_V2.map((opt) => (
              <motion.button key={opt} className={cn(picked === opt && 'pick')} variants={pop} whileTap={{ scale: 0.96 }} onClick={() => pick(opt)}>
                {opt}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>
        {msg && <motion.div className="pd-done" variants={fadeUp}>{msg}</motion.div>}

        {/* X's founder cards — brought back into X+1 (PO: "the old version cards
            were awesome"). The use-of-proceeds pie moved to its own slide, which is
            what makes room for these here. */}
        <motion.div className="pd-founders" variants={stagger}>
          <motion.div className="pd-fcard" variants={fadeUp}>
            <img src="/pitch/david.jpg" alt="David Demmer" />
            <div className="fi">
              <h4>David Demmer</h4>
              <div className="fr">Co-founder · CEO &amp; CTO</div>
              <a href={`mailto:${FOUNDER_LINKS_V2.david.email}`}>{FOUNDER_LINKS_V2.david.email}</a>
              <div className="flinks">
                <a href={FOUNDER_LINKS_V2.david.calendar} target="_blank" rel="noopener noreferrer">Book a call</a>
                <a href={FOUNDER_LINKS_V2.david.dataroom} target="_blank" rel="noopener noreferrer">Data room</a>
              </div>
            </div>
          </motion.div>
          <motion.div className="pd-fcard" variants={fadeUp}>
            <img src="/pitch/simon.jpg" alt="Simon Stegelmeier" />
            <div className="fi">
              <h4>Simon Stegelmeier</h4>
              <div className="fr">Co-founder · CMO &amp; Analytics</div>
              <a href={`mailto:${FOUNDER_LINKS_V2.simon.email}`}>{FOUNDER_LINKS_V2.simon.email}</a>
              <div className="flinks">
                <a href={FOUNDER_LINKS_V2.simon.calendar} target="_blank" rel="noopener noreferrer">Book a call</a>
                <a href={FOUNDER_LINKS_V2.simon.dataroom} target="_blank" rel="noopener noreferrer">Data room</a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * X+1 · TITLE — adds the decision-maker email (rule 45); keeps v2026.09.
 * ====================================================================== */
export function S0TitleV2({ dir }: { dir: number }) {
  return (
    <Shell
      className="pd-title"
      custom={dir}
      bg={
        <>
          <img className="pd-img" src="/pitch/artsy/c.jpg" alt="Raisy" />
          <div className="pd-scrim" />
        </>
      }
    >
      <motion.div className="pd-title-inner" variants={stagger} initial="hidden" animate="show">
        <Kicker>Initializing Raisy…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Raisy
          <small>The AI workforce that runs companies</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          AI prepares. Humans decide. Trust is earned.
        </motion.p>
        <motion.div className="pd-chips" variants={stagger}>
          <motion.span className="pd-chip" variants={pop}>david@raisy.team</motion.span>
          <motion.span className="pd-chip" variants={pop}>raisy.team</motion.span>
          <motion.span className="pd-chip muted" variants={pop}>Hong Kong</motion.span>
          <motion.span className="pd-chip" variants={pop}>v2026.09</motion.span>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * X+1 · PASTE YOUR LINK — labels 3,552 as internal product data (RP51).
 * ====================================================================== */
export function S5PasteLinkV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_product.jpg', alt: 'Raisy reading your site', cap: 'the product' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Onboarding…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Paste your link. We do the rest.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Your website becomes your company. <b>3,552 investors scored.</b> Emails built for a raise.
        </motion.p>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 26 }} variants={stagger}>
          <Stat
            n={<span style={{ fontSize: 'clamp(16px,1.8vw,22px)' }}>One link. No forms. No calls.</span>}
            l="We read your site and build your profile."
          />
          <Stat
            n={<span style={{ fontSize: 'clamp(16px,1.8vw,22px)' }}>Nothing leaves without you.</span>}
            l="You approve. It sends. It tracks."
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * X+1 · MARKET — THE NUMBERS COME FROM THE FINANCIAL MODEL (D-066).
 *
 * Source of truth: the generated `./marketByYear.ts`, whose provenance header names
 * the model file, the assumption inputs and the basis of each series (do NOT restate
 * those paths here — they moved once already). The 30-value 5-year series comes from
 * the model behind the ORBIT dashboard. Regenerate, never hand-edit.
 *
 * WHAT CHANGED: the MECHANISM, not the numbers. This slide used to carry hand-entered
 * figures (TAM $52.6B from MarketsandMarkets; SAM $72M = 40,000 founders raising x
 * ~$1.8k; SOM $9M = 5,000 accounts x ~$1.8k). It now reads them from the generated
 * file, which is the change that matters: the numbers can no longer drift apart.
 * PO instruction 2026-09-14: the model's numbers are the ones that flow into the deck.
 *
 * ⚠️ THIS COMMENT WAS ITSELF STALE (found 2026-10-11, @P288 T0300). It used to read
 * that the hand-entered figures "matched NO model", and that the model's SAM basis was
 * "2,000,000 US solo companies x $1,800 = $3.6B, i.e. 50x the old figure". Neither
 * half holds against the generated file this slide actually reads: it carries
 * TAM $52.6B (2030) and SAM $72M (2026) -> $360M (2030), i.e. exactly the figures
 * described above as unmatched. "$3.6B" appears in NO model file. The stale claim
 * misled a reconciliation pass in the FM-0013 kernel into marking the model's own
 * TAM and SAM rows false; that pass is corrected. Recorded here because a comment
 * that describes a superseded basis is indistinguishable from a current one.
 * ====================================================================== */

/** USD → the CountUp's display value + suffix. Raw dollars NEVER go to CountUp
 *  (it formats whatever it is given, which once printed "$52600000000.0B"). */
const compact = (usd: number) =>
  usd >= 1e9
    ? { dv: usd / 1e9, suf: 'B', dec: 1 }
    : usd >= 1e6
      ? { dv: usd / 1e6, suf: 'M', dec: 1 }
      : { dv: usd / 1e3, suf: 'K', dec: 0 };

/** the same figure as plain text, for prose — a hard-coded `B` in the note under
 *  the diagram printed "SAM 0.1B" once SAM stopped being in billions. */
const short = (usd: number) => {
  const c = compact(usd);
  return `${c.dv.toFixed(c.dec)}${c.suf}`;
};

const LAST = MARKET_YEARS.length - 1;
const FIRST_YEAR = MARKET_YEARS[0];
const LAST_YEAR = MARKET_YEARS[LAST];
const tamNow = MARKET_TAM[LAST];
const samNow = MARKET_SAM[LAST];
const somNow = MARKET_SOM[LAST];
/** the widest ratio the figure has to survive — drives the geometry AND its note */
const RANGE = tamNow / somNow;
const TRUE_SOM_PX = (152 * Math.sqrt(somNow / tamNow)).toFixed(1);

const MARKET = [
  {
    key: 'tam',
    v: tamNow,
    ...compact(tamNow),
    pre: '$',
    fs: 'clamp(22px,2.8vw,34px)',
    sub: `${LAST_YEAR} · +${(MARKET_TAM_GROWTH[LAST]! * 100).toFixed(0)}%/yr`,
    lbl: `TAM · ${FIRST_YEAR}–${LAST_YEAR}`,
    src: `TAM — the AI-agent software market, ${FIRST_YEAR} → ${LAST_YEAR} at the model's endpoint CAGR (${(MARKET_TAM_GROWTH[LAST]! * 100).toFixed(1)}%/yr). Source: Raisy financial model (raisyModel.ts).`,
  },
  {
    key: 'sam',
    v: samNow,
    ...compact(samNow),
    pre: '$',
    fs: 'clamp(16px,2vw,23px)',
    /* ⚠️ THE CAGR WAS MISSING ON TWO OF THE THREE RINGS (PO 2026-10-09: *"SAM and SOM CAGR is
       missing"*). TAM printed `+46%/yr` in the same type size and position, so the visual grammar
       said "this ring has growth and those two do not" — when in fact the model carries all three
       (`MARKET_CAGR.sam` 50%/yr, `.som` 172%/yr). The basis figures are kept; the CAGR joins them. */
    sub: `${Math.round(MARKET_SAM_BASIS.companiesPerYear / 1000)}k × ~$${(MARKET_SAM_BASIS.spendPerCompany / 1000).toFixed(1)}k/yr · +${(MARKET_CAGR.sam * 100).toFixed(0)}%/yr`,
    lbl: 'SAM · founders raising annually',
    /* WAS "= $72M, held flat across the window" — false since the model began
       accumulating cohorts (`samCohorts = 1 + (year − sam_base_year)`,
       raisyModel.ts:489): SAM is $72M in 2026 → $360M by 2030, +49.5%/yr. The deck
       was asserting the opposite of its own source, in the one string that also
       prints into the PDF. Now derived from the generated mirror. */
    src: `SAM — ${MARKET_SAM_BASIS.companiesPerYear.toLocaleString('en-US')} founders raising annually × ~$${MARKET_SAM_BASIS.spendPerCompany.toLocaleString('en-US')}/yr, one cohort added each year (a cohort keeps raising, so it stays addressable): ${short(MARKET_SAM[0])} in ${MARKET_SAM_BASIS.baseYear} → ${short(samNow)} by ${LAST_YEAR}, ${(MARKET_CAGR.sam * 100).toFixed(1)}%/yr. Source: Raisy financial model (raisyModel.ts).`,
  },
  {
    key: 'som',
    v: somNow,
    ...compact(somNow),
    pre: '$',
    /* SOM's `fs` was missing, so this value rendered at whatever size it inherited
       (found 2026-09-14 by `tsc`, which reported the union member lacking `fs` — the
       live slide still *looked* plausible, which is why it survived the visual pass). */
    fs: 'clamp(13px,1.7vw,18px)',
    sub: `${LAST_YEAR} · +${(MARKET_CAGR.som * 100).toFixed(0)}%/yr · ${MARKET_CUSTOMERS[LAST].toLocaleString()} accts`,
    lbl: 'SOM · bottom-up from the customer build',
    src: `SOM — paying customers × ~$${MARKET_SAM_BASIS.spendPerCompany.toLocaleString('en-US')} blended ARPU, bottom-up from the signup/conversion/churn build: ${MARKET_SOM_GROWTH.map((g, i) => (g === null ? `${MARKET_YEARS[i]} n/a` : `${MARKET_YEARS[i]} +${(g * 100).toFixed(0)}%`)).join(' · ')}. Source: Raisy financial model (raisyModel.ts).`,
  },
];

/** THE ONE PLACE THE MARKET GEOMETRY IS DECIDED (D-064/D-067) — the same function
 *  the X deck (`slides.tsx`) uses, so every version draws the same market.
 *
 * `glowtest_FE/src/components/viewers/glow_10_viewer_tamsamsom.js` sizes its
 * circles AREA-TRUE — `r = maxRadius · √(value / tamValue)`. Applied here that
 * gives SAM ~42 px (fine) but SOM ~${TRUE_SOM_PX} px (invisible) because the range is ${RANGE.toFixed(0)}×.
 * A true-area drawing is therefore impossible; the radii sit on a DECLARED LOG
 * scale — monotonic, order-preserving, and the slide states it. Change this
 * function and the drawing follows; change the scale and the note under the
 * diagram must change with it. */
const R_MAX = 152;
const R_MIN = 54;
function marketRadius(value: number): number {
  const logs = MARKET.map((m) => Math.log10(m.v));
  const lo = Math.min(...logs);
  const hi = Math.max(...logs);
  return R_MIN + ((Math.log10(value) - lo) / (hi - lo)) * (R_MAX - R_MIN);
}

export function S9MarketV2({ dir }: { dir: number }) {
  const [active, setActive] = useState<string | null>(null);
  /* The source rides the CURSOR as a fixed overlay. The old version rendered it in
     the slide's own flow, so its height changed with the text and `Shell`'s
     auto-fit scaled the whole slide — every hover moved everything (reported
     2026-09-14). An overlay cannot affect layout; a fixed position cannot scroll. */
  const [tip, setTip] = useState({ x: -1, y: -1 });
  const detail = MARKET.find((b) => b.key === active);
  const moveTip = (e: React.MouseEvent) => {
    const w = 360;
    setTip({
      x: Math.max(12, Math.min(e.clientX + 16, window.innerWidth - w - 18)),
      y: Math.min(e.clientY + 18, window.innerHeight - 150),
    });
  };
  return (
    <Shell custom={dir} className="pd-s2">
      <motion.div variants={stagger} initial="hidden" animate="show" className="pd-row">
        <Kicker>Sizing the market…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          A global market, with a focused wedge.
        </motion.h1>
        {/* CONCENTRIC, bottom-anchored — the house geometry (glowtest_FE
            `glow_10_viewer_tamsamsom.js`): every circle shares ONE baseline and
            is centred, so TAM contains SAM contains SOM. The stylesheet already
            positions `.pd-bb-c` that way (left:50% / bottom:0 / translateX(-50%))
            — the earlier inline override that forced a side-by-side row is gone.
            Radii come from `marketRadius()`; nothing here is a literal. */}
        <motion.div
          className="pd-bb"
          variants={fadeUp}
          onMouseMove={moveTip}
          style={{ position: 'relative', width: R_MAX * 2, height: R_MAX * 2, margin: '14px auto 0' }}
        >
          {MARKET.map((b, i) => {
            const r = Math.round(marketRadius(b.v));
            const d = r * 2;
            /* the band a ring owns before the next circle starts — the label lives
               in that band, so the position is DERIVED, never a fixed percentage. */
            const nextR = i < MARKET.length - 1 ? marketRadius(MARKET[i + 1].v) : 0;
            const band = 2 * (r - nextR);
            return (
              <motion.div
                key={b.key}
                className={cn('pd-bb-c', b.key, active === b.key && 'on')}
                initial={{ opacity: 0, scale: 0.72 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 120, damping: 16, delay: 0.5 + i * 0.45 }}
                onMouseEnter={() => setActive(b.key)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive((a) => (a === b.key ? null : b.key))}
                /* CENTRED BY ARITHMETIC, not by a CSS transform: framer writes its
                   own `transform` for the scale animation and silently drops the
                   stylesheet's `translateX(-50%)`, which pushed every circle right
                   by its own radius (caught 2026-09-14 — measured centres 196/147/133
                   instead of one shared axis). left = R_MAX - r puts the centre of a
                   2r-wide circle on the container's centreline, always. */
                style={{ width: d, height: d, zIndex: i + 1, left: R_MAX - r, bottom: 0 }}
              >
                <span className="pl" style={{ top: Math.round(band * 0.16), left: '50%', transform: 'translateX(-50%)' }}>
                  {b.key.toUpperCase()}
                </span>
                <span
                  className="pv"
                  style={{ fontSize: b.fs, top: Math.round(band * 0.36), left: '50%', transform: 'translateX(-50%)' }}
                >
                  <CountUp to={b.dv} prefix={b.pre} suffix={b.suf} decimals={b.dec} duration={3.2} />
                </span>
                {/* the band is thin for middle rings — the caption row below carries it then */}
                {band >= 62 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: Math.round(band * 0.36) + 26,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: 'clamp(9px,0.85vw,11px)',
                      opacity: 0.75,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.sub}
                  </span>
                )}
              </motion.div>
            );
          })}
        </motion.div>
        <motion.div className="pd-bb-labels" variants={fadeUp}>
          {MARKET.map((b) => (
            <span key={b.key}>
              <b>{b.key.toUpperCase()}</b> {b.lbl.split('·')[1]}
            </span>
          ))}
        </motion.div>
        {/* PRINT ONLY. On screen the sources live in the cursor tooltip, which a PDF
            cannot capture — so the printed page must carry all three, in full. */}
        <div className="pd-print-only pd-bb-sources">
          {MARKET.map((b) => (
            <p key={b.key}>{b.src}</p>
          ))}
        </div>
        {/* the 30 values themselves — 6 series x 5 years — reach the PDF even though
            the presented slide stays visual (see ./marketTable.tsx) */}
        <MarketByYearTable />
        <motion.p className="pd-fine" variants={fadeUp}>
          Pulled from the Raisy financial model — {FIRST_YEAR}–{LAST_YEAR}, {MARKET_YEARS.length} years:
          TAM {short(MARKET_TAM[0])} → {short(tamNow)}
          ({(MARKET_CAGR.tam * 100).toFixed(0)}%/yr) · SAM {short(MARKET_SAM[0])} → {short(samNow)}
          ({(MARKET_CAGR.sam * 100).toFixed(0)}%/yr) · SOM {short(MARKET_SOM[0])} → {short(somNow)}
          ({(MARKET_CAGR.som * 100).toFixed(0)}%/yr). Circles are concentric
          on one baseline, radii on a <b>log scale</b> — TAM→SOM spans {RANGE.toFixed(0)}×, so a true-area
          drawing would render SOM at {TRUE_SOM_PX} px. Radial order is honest; only the spacing between
          rings is compressed. Hover a circle for its source.
        </motion.p>
        {/* the source follows the CURSOR as a fixed overlay — it is out of the layout
            flow, so hovering can never change the slide's height and the auto-fit can
            never re-scale it (the old in-flow block did both — reported 2026-09-14). */}
        {detail && tip.x > 0 && (
          <div className="pd-bb-tip" style={{ left: tip.x, top: tip.y }}>
            {detail.src}
          </div>
        )}
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * X+1 · TEAM + ADVISORS on ONE slide — his p15 composition. The advisors are
 * LIVE cards, not the PDF's baked raster (that one cropped to half the names in
 * a side column). Portraits are the same assets `Playano_Ed_HP_4` uses.
 * ====================================================================== */
/* EXPORTED so the master's team slide reads the SAME list rather than keeping a second copy of
 * four image URLs (`DRAISY` MR0pv: a second home for one fact is a defect). Additive only — deck
 * C's own rendering is untouched. */
export const ADVISORS = [
  { n: 'David Goh', r: 'Legal', img: 'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/advisor_Dave.jpeg' },
  { n: 'Alasdair Pocock', r: 'Creative & Strategy', img: 'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/Advisor_Ally.jpeg' },
  { n: 'Tai Huynh, PhD', r: 'Technical', img: 'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/Tai.jpeg' },
  { n: 'Wynn Wu', r: 'Product', img: 'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/advisor_wynn2.jpeg' },
];

export function S12TeamV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} className="pd-s13">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Loading team profiles…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Why we are the right team for this.
          <small>9 shipped products · 1 exit · 4 accelerators. Raisy is the one that runs the company; we are raising on it ourselves.</small>
        </motion.h1>
        {/* two columns, not the default 3-col grid (which is sized for the old
            founders+Playano layout) — and never 1 col: .pd-tcard img is width:100%
            with aspect-ratio 1/0.9, so a single column makes each photo ~970px tall. */}
        <motion.div className="pd-team" style={{ gridTemplateColumns: '1fr 1fr' }} variants={stagger}>
          <motion.div className="pd-tcard" variants={fadeUp}>
            <img src="/pitch/david.jpg" alt="David Demmer" />
            <div className="tinfo">
              <h3>David Demmer</h3>
              <div className="role">Co-Founder · CEO &amp; CTO</div>
              <p>
                Left his CEO position at a unicorn to focus on Playano. Went through 1
                incubation program and 3 acceleration programs. Shipped 9 products across
                the Playano suite. Awarded hedge-fund manager, having managed over $4B AUM
                multi-asset under SFC license. Ex-CIO/Co-Founder CryptAM (exited /
                Cyberport hall of fame).
              </p>
            </div>
          </motion.div>
          <motion.div className="pd-tcard" variants={fadeUp}>
            <img src="/pitch/simon.jpg" alt="Simon Stegelmeier" />
            <div className="tinfo">
              <h3>Simon Stegelmeier</h3>
              <div className="role">Co-Founder · CMO &amp; Analytics</div>
              <p>
                10+ years building enterprise-grade data workflows (Salesforce, Tableau,
                Data Cloud) for Vaillant, LEGO, ESA, and Accenture. He built the governance
                layer — kernel, memory, audit trail — that makes Raisy enterprise-ready.
              </p>
            </div>
          </motion.div>
        </motion.div>

        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontFamily: 'var(--pd-mono)',
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--pd-dim)',
              marginBottom: 9,
            }}
          >
            Advisors
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
            {ADVISORS.map((a) => (
              <div key={a.n} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src={a.img}
                  alt={a.n}
                  style={{ width: 46, height: 46, borderRadius: 10, objectFit: 'cover', flex: '0 0 46px' }}
                />
                <div>
                  <div style={{ fontSize: 'clamp(12px,1.15vw,14px)', fontWeight: 700, color: 'var(--pd-fg)' }}>{a.n}</div>
                  <div style={{ fontSize: 'clamp(10px,1vw,12px)', color: 'var(--pd-dim)' }}>{a.r}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * X+1 · PROOF — USD (D-055) and relative dates instead of three hard ones.
 * ====================================================================== */
const MILESTONES_V2 = [
  { num: 'Q4 2026', title: 'Launch v1', desc: 'and begin raising on Raisy' },
  { num: '+3 weeks', title: '100+ investors', desc: 'researched · 25 meetings booked' },
  { num: '+6 weeks', title: '10+ founders', desc: 'onboarded · first case study published' },
  { num: 'Month 6', title: '100 paying', desc: 'first repeatable pipeline' },
  { num: 'Month 12', title: `${M12_CUSTOMERS.toLocaleString('en-US')} paying`, desc: `$${(M12_ARR / 12 / 1000).toFixed(0)}K MRR · $${(M12_ARR / 1e6).toFixed(1)}M ARR` },
];

export function S11ProofV2({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_traction.jpg', alt: 'Raisy with rising metrics', cap: 'the proof' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Pulling the proof…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          We're not asking you to believe us. We're showing you.
        </motion.h1>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 20 }} variants={stagger}>
          <Stat n={<span style={{ fontSize: 'clamp(14px,1.5vw,18px)' }}>Design Partner</span>} l={<>Jonathan Weins, Founder/CEO of <b>PopMeals</b>.</>} />
          <Stat n={<span style={{ fontSize: 'clamp(14px,1.5vw,18px)' }}>Community</span>} l={<>Selected for <b>SuperCharger Ventures</b>.</>} />
          <Stat n={<span style={{ fontSize: 'clamp(14px,1.5vw,18px)' }}>Dogfooding</span>} l={<>Running our own <b>$${(PROCEEDS_ROUND / 1e6).toFixed(1)}M raise on Raisy</b>.</>} />
        </motion.div>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1], delay: 0.8 }} style={{ transformOrigin: 'left' }} />
          </div>
          <div className="pd-jrow">
            {MILESTONES_V2.map((m, i) => (
              <motion.div
                key={m.num}
                className="pd-jnode"
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.9 + i * 0.35 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * X+1 · USE OF PROCEEDS — its own slide, so the donut and the legend are
 * readable (PO: a squeezed pie is a failure). Mirrors the pre-PDF deck's
 * `pd-alloc` layout, which already sized this well.
 * ====================================================================== */
export function S15ProceedsV2({ dir }: { dir: number }) {
  const [slice, setSlice] = useState<string | null>(null);
  return (
    <Shell custom={dir} className="pd-ask pd-alloc">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Allocating the round…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Raising $${(PROCEEDS_ROUND / 1e6).toFixed(1)}M pre-seed
          <small>Where it goes</small>
        </motion.h1>
        <motion.div className="pd-ask-col" variants={stagger}>
          <motion.div className="pd-pie-wrap" variants={fadeUp}>
            <DonutV2 active={slice} onActive={setSlice} size={330} />
          </motion.div>
          <motion.div className="pd-breakdown" variants={fadeUp}>
            {PIE_V2.map((d) => (
              <div
                className={cn('brow', slice === d.name && 'on')}
                key={d.name}
                onMouseEnter={() => setSlice(d.name)}
                onMouseLeave={() => setSlice(null)}
                onClick={() => setSlice(slice === d.name ? null : d.name)}
                role="button"
                tabIndex={0}
              >
                <span className="sw" style={{ background: d.color }} />
                <span className="bn">{d.name}</span>
                <span className="bp">{d.value}%</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp} style={{ marginTop: 16, fontSize: 'clamp(12px,1.25vw,15px)' }}>
          {PROCEEDS.map((p) => `${Math.round(p.pct * 100)}% ${p.label}`).join(' · ')}
          {' '}— an 18-month runway, no success fees, no equity.
        </motion.p>
        {/* The go-to-market affordability answer (FM-0013 D-027), read from the model.
            "Isn't it too costly to go to market?" is only answerable if the budget has a
            source — here the ceiling is derived from ARPU, gross margin and churn, so it
            is not a claim the deck asserts about itself. */}
        <motion.p
          className="pd-sub"
          variants={fadeUp}
          style={{ marginTop: 6, fontSize: 'clamp(11px,1.1vw,13px)', color: 'var(--pd-dim)' }}
        >
          The ${(GTM_BUDGET / 1000).toFixed(0)}K marketing &amp; sales slice buys{' '}
          <b>{GTM_CUSTOMERS_THE_ROUND_BUYS.toLocaleString('en-US')}</b> customers at the modelled ${GTM_CAC_TODAY} CAC.
          {' '}Payback and LTV/CAC put the ceiling at ${Math.round(GTM_CAC_CEILING)} — acquisition can get{' '}
          <b>{Math.round(GTM_CAC_HEADROOM_PCT * 100)}%</b> more expensive before the 12-month payback test fails.
        </motion.p>
        {/* The valuation METHOD (FM-0013 D-031/D-035). This slide used to restate the
            post-money price and never say where the price came from — the one question
            a VC persona asks first. The model prices the business off the year-5 EXIT;
            the DCF is a technical cross-check, not the method (published practice for
            early-stage companies, where a DCF is typically not used at all). */}
        <motion.p
          className="pd-sub"
          variants={fadeUp}
          style={{ marginTop: 6, fontSize: 'clamp(10.5px,1.05vw,12.5px)', color: 'var(--pd-dim)' }}
        >
          Valued off the <b>year-{EXIT_HORIZON_YEARS} exit plan</b> ({EXIT_EV_MULTIPLE.toFixed(1)}× year-{EXIT_HORIZON_YEARS}{" "}
          <b>ARR</b>, the run-rate the exit is priced on rather than the revenue earned across the year) — the method
          used for early-stage rounds, not a discounted cash flow. That {EXIT_EV_MULTIPLE.toFixed(1)}× is a deliberate discount to listed
          software, which traded at {EXIT_EV_MULTIPLE_BAND.high.toFixed(1)}× sales in January 2026 (Damodaran); the band is {EXIT_EV_MULTIPLE_BAND.low.toFixed(1)}×–{EXIT_EV_MULTIPLE_BAND.high.toFixed(1)}×, and this price
          still clears our target down to {EXIT_MULTIPLE_REQUIRED.toFixed(1)}×. On that plan a ${(TICKET_SIZE / 1000).toFixed(0)}K ticket
          returns <b>{ROUND_MOIC.toFixed(1)}×</b>, i.e.{' '}
          <b>{((Math.pow(ROUND_MOIC, 1 / EXIT_HORIZON_YEARS) - 1) * 100).toFixed(0)}%/yr</b> over the horizon, before any
          dilution from a later round.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * V2 REGISTRY — V1's slides + the X+1 changes above = 15 registry + 3 = 18.
 * ====================================================================== */
export const DECK_SLIDES_V2: Array<(p: { dir: number }) => ReactElement> = [
  S0TitleV2,
  S1Hook,
  S1HookReveal,
  S2EraV2,
  S3CompanyOSV2,
  S4ShipV2,
  S5PasteLinkV2,
  S6TrustLayer,
  S7Standards,
  S8ToolVsOSV2,
  S9MarketV2,
  S10PricingV2,
  S11ProofV2,
  S12TeamV2,
  S15ProceedsV2,
];

export const PRELOAD_V2 = [
  '/pitch/artsy/c.jpg',
  '/pitch/deck/hook_founders_grey.jpg',
  '/pitch/deck/hook_founders_colour.jpg',
  '/pitch/s_abstract_network.jpg',
  '/pitch/s_abstract_pipeline.jpg',
  '/pitch/s_product.jpg',
  '/pitch/s_vision.jpg',
  '/pitch/s_moat.jpg',
  '/pitch/s_traction.jpg',
  '/pitch/david.jpg',
  '/pitch/simon.jpg',
  'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/advisor_Dave.jpeg',
  'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/Advisor_Ally.jpeg',
  'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/Tai.jpeg',
  'https://playanoassets.s3.ap-east-1.amazonaws.com/playano_hp/advisor_wynn2.jpeg',
  '/pitch/raisy_white.png',
];

/** V2 reuses V1's close + impressum slides unchanged. */
export { S17Impressum, S18CompanyOS };
